import { TrendingUp, TrendingDown } from "lucide-react";

const COMPONENT_LABELS = {
  trend: "Trend",
  momentum: "Momentum",
  volume: "Volume",
  volatility: "Volatility",
  relative_strength: "Relative Strength",
  breakout: "Breakout",
  mean_reversion: "Mean Reversion",
  liquidity: "Liquidity",
  market_regime: "Market Regime",
  risk_reward: "Risk / Reward",
};

function fmt(v, d = 1) {
  if (v == null) return "—";
  return Number(v).toFixed(d);
}

export default function ScoreBreakdown({ result }) {
  if (!result || !result.contributions) return null;
  const isShort = result.signal === "SHORT";
  const focusKey = isShort ? "short_contribution" : "long_contribution";
  // Sort by signed contribution (positive first = pro-long/pro-signal)
  const contribs = [...result.contributions].sort((a, b) => {
    const av = isShort ? b.short_contribution - a.short_contribution : b.long_contribution - a.long_contribution;
    return av;
  });
  const positives = contribs.filter((c) =>
    isShort ? c.signed_contribution < 0 : c.signed_contribution > 0
  );
  const negatives = contribs.filter((c) =>
    isShort ? c.signed_contribution > 0 : c.signed_contribution < 0
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3">
      {/* Component bars */}
      <div
        data-testid="score-breakdown-bars"
        className="border border-[#1E293B] bg-[#0F1626]"
      >
        <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C]">
          <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
            Component Scores · Long vs Short · Weight × Score = Contribution
          </span>
        </div>
        <div className="p-3 space-y-2">
          {result.contributions.map((c) => (
            <div key={c.component} className="grid grid-cols-[130px_1fr_120px] gap-3 items-center">
              <div className="mono text-[11px] text-slate-200 uppercase tracking-wider truncate">
                {COMPONENT_LABELS[c.component] || c.component}
                <span className="mono text-[9px] text-slate-600 ml-1">·{c.weight}%</span>
              </div>
              {/* Mirrored bar: short grows LEFT from centre, long grows RIGHT from centre.
                  Each half is 50% of the track width; scale = score% * 0.5. */}
              <div className="relative h-4 bg-[#151C2C] overflow-hidden">
                {/* left half = short */}
                <div
                  className="absolute top-0 bottom-0 bg-red-500/70"
                  style={{
                    right: "50%",
                    width: `${(c.short_score / 2)}%`,
                    minWidth: c.short_score > 0 ? "2px" : 0,
                  }}
                  data-testid={`score-short-${c.component}`}
                />
                {/* right half = long */}
                <div
                  className="absolute top-0 bottom-0 bg-emerald-500/70"
                  style={{
                    left: "50%",
                    width: `${(c.long_score / 2)}%`,
                    minWidth: c.long_score > 0 ? "2px" : 0,
                  }}
                  data-testid={`score-long-${c.component}`}
                />
                <div className="absolute inset-y-0 left-1/2 w-px bg-slate-500/70" />
              </div>
              <div className="text-right mono text-[11px] tabular-nums flex gap-2 justify-end">
                <span className="text-emerald-300">{fmt(c.long_score)}</span>
                <span className="text-slate-600">/</span>
                <span className="text-red-300">{fmt(c.short_score)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Signed evidence list */}
      <div className="border border-[#1E293B] bg-[#0F1626]">
        <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C]">
          <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
            Evidence · {isShort ? "Pro-Short" : "Pro-Long"} vs Opposite
          </span>
        </div>
        <div className="p-3 space-y-3">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <TrendingUp className="h-3 w-3 text-emerald-400" />
              <span className="mono text-[10px] uppercase tracking-widest text-emerald-300">
                Supporting {isShort ? "Short" : "Long"}
              </span>
            </div>
            {positives.length === 0 && (
              <div className="mono text-[10px] text-slate-600 italic">— none —</div>
            )}
            {positives.map((c) => (
              <div key={"p-" + c.component} className="flex items-center justify-between py-1 border-b border-[#151C2C] last:border-b-0">
                <span className="mono text-[10px] text-slate-300">{COMPONENT_LABELS[c.component] || c.component}</span>
                <span className="mono text-[10px] text-emerald-300 tabular-nums">
                  {isShort ? "+" : "+"}{fmt(Math.abs(c.signed_contribution), 2)}
                </span>
              </div>
            ))}
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <TrendingDown className="h-3 w-3 text-red-400" />
              <span className="mono text-[10px] uppercase tracking-widest text-red-300">
                Against {isShort ? "Short" : "Long"}
              </span>
            </div>
            {negatives.length === 0 && (
              <div className="mono text-[10px] text-slate-600 italic">— none —</div>
            )}
            {negatives.map((c) => (
              <div key={"n-" + c.component} className="flex items-center justify-between py-1 border-b border-[#151C2C] last:border-b-0">
                <span className="mono text-[10px] text-slate-300">{COMPONENT_LABELS[c.component] || c.component}</span>
                <span className="mono text-[10px] text-red-300 tabular-nums">
                  −{fmt(Math.abs(c.signed_contribution), 2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
